'use client';

import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kit/ui/tooltip';
import { cn } from '@kit/ui/utils';

export interface AvatarGroupParticipant {
  user_id: string;
  name: string;
  avatarUrl?: string | null;
  initials: string;
}

interface AvatarGroupProps {
  participants: AvatarGroupParticipant[];
  /** Maximum number of avatars to display before showing +N */
  maxVisible?: number;
  /** Size of each avatar */
  size?: 'sm' | 'md' | 'lg';
  /** Called when clicking on the group or overflow indicator */
  onShowAll?: () => void;
  className?: string;
}

const sizeClasses = {
  sm: 'h-7 w-7 text-[10px]',
  md: 'h-8 w-8 text-xs',
  lg: 'h-10 w-10 text-sm',
} as const;

const overlapClasses = {
  sm: '-ml-2',
  md: '-ml-2.5',
  lg: '-ml-3',
} as const;

/**
 * AvatarGroup displays a stacked group of participant avatars
 * with tooltip showing each participant's name on hover.
 *
 * Features:
 * - Overlapping stack layout
 * - Tooltip on hover showing participant name
 * - +N indicator for overflow participants
 * - Click to show all participants
 */
export function AvatarGroup({
  participants,
  maxVisible = 4,
  size = 'md',
  onShowAll,
  className,
}: AvatarGroupProps) {
  const visibleParticipants = participants.slice(0, maxVisible);
  const overflowCount = participants.length - maxVisible;
  const hasOverflow = overflowCount > 0;

  // Generate tooltip for overflow showing hidden participant names
  const overflowNames = hasOverflow
    ? participants
        .slice(maxVisible)
        .map((p) => p.name)
        .join(', ')
    : '';

  const containerProps = onShowAll
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: onShowAll,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onShowAll();
          }
        },
      }
    : {};

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={cn(
          'flex items-center',
          onShowAll && 'cursor-pointer',
          className,
        )}
        {...containerProps}
      >
        {visibleParticipants.map((participant, index) => (
          <Tooltip key={participant.user_id}>
            <TooltipTrigger asChild>
              <Avatar
                className={cn(
                  sizeClasses[size],
                  'ring-background ring-2 transition-transform hover:z-10 hover:scale-110',
                  index > 0 && overlapClasses[size],
                )}
              >
                <AvatarImage
                  src={participant.avatarUrl || undefined}
                  alt={participant.name}
                />
                <AvatarFallback className="bg-muted text-muted-foreground font-medium">
                  {participant.initials}
                </AvatarFallback>
              </Avatar>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="font-medium">
              {participant.name}
            </TooltipContent>
          </Tooltip>
        ))}

        {hasOverflow && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  sizeClasses[size],
                  overlapClasses[size],
                  'bg-muted text-muted-foreground ring-background flex items-center justify-center rounded-full ring-2 transition-transform hover:z-10 hover:scale-110',
                  'font-medium',
                )}
              >
                +{overflowCount}
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[200px] font-medium">
              {overflowNames}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
